import { useCallback, useMemo } from 'react';
import { useRouter } from 'next/router';
import { signOut, useSession } from '@ghq-abi/auth-client-lib/client';
import { useTranslate } from '@tolgee/react';
import nprogress from 'nprogress';

import { useAbility } from '~/app/contexts/AbilityContext';
import type { User } from '~/app/contexts/UserContext';
import { Icon } from '~/shared/components';
import { SignOut } from '~/shared/components/icons/SignOut';
import { lcmApi } from '~/shared/services/api';

import { useGetProfilePicture } from '../../../../entities/Employee';

export const useUserDropdown = (user: User) => {
  const { t } = useTranslate(['default']);
  const { proxyOut } = useSession();
  const router = useRouter();
  const ability = useAbility();

  const isProxying = user?.isProxying;

  const handleSignOut = useCallback(async () => {
    try {
      await lcmApi.post('/abilities/clear-cache', {
        employeeId: user?.employeeId,
      });
    } finally {
      signOut({ redirect: false });
    }
  }, [user?.employeeId]);

  const goToProxy = useCallback(async () => {
    router.push(
      `/proxy?callBackUrl=${encodeURIComponent(window.location.pathname)}`
    );
  }, [router]);

  const handleStopProxy = useCallback(async () => {
    nprogress.start();

    try {
      await proxyOut();

      window.location.reload();
    } catch (error) {
      console.error('Error stopping proxy:', error);
    } finally {
      nprogress.done();
    }
  }, [proxyOut]);

  const items = useMemo(() => {
    const dropdownItems = [
      {
        label: t('common.sign_out'),
        onSelect: handleSignOut,
        icon: <SignOut />,
      },
    ];

    if (ability.can('proxy', 'User')) {
      if (isProxying) {
        dropdownItems.unshift(
          {
            label: t('common.switch_proxy'),
            onSelect: goToProxy,
            icon: <Icon.Proxy size={18} />,
          },
          {
            label: t('common.stop_proxy'),
            onSelect: handleStopProxy,
            icon: <Icon.StopProxy size={18} />,
          }
        );
      } else {
        dropdownItems.unshift({
          label: t('common.proxy_as_user'),
          onSelect: goToProxy,
          icon: <Icon.Proxy size={18} />,
        });
      }
    }

    return dropdownItems;
  }, [ability, goToProxy, handleStopProxy, isProxying, t, handleSignOut]);

  const { imageSrc } = useGetProfilePicture(user.globalId ?? '');

  const profileInfo = {
    name: user.name ?? '',
    urlImage: imageSrc ?? undefined,
    zone: user.zone ?? '',
  };

  return {
    items,
    profileInfo,
  };
};
